import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { take } from 'rxjs/operators';

import { filterBoolean, isExecutableFile } from '@recruitee/common';
import {
  Attachment,
  MailboxMessageAttachmentAssignment,
  VisibilityLevel,
} from '@recruitee/http/types';
import { I18nService } from '@recruitee/i18n';

import { WindowRef } from '../../../common/window-ref';
import { DialogWidth } from '../../../dialog/dialog-config';
import { DialogService } from '../../../dialogs/dialogs.module';
import { IconName } from '../../../icons-container/icons.type';
import {
  AttachmentPreviewDialogComponent,
  AttachmentPreviewDialogData,
} from '../attachment-preview-dialog/attachment-preview-dialog.component';
import { AttachmentType } from './attachment-item.types';

interface AttachmentAction {
  label: string;
  icon: IconName;
  variant?: string;
  handler: () => void;
}

export interface AttachmentItemTranslations {
  setAsCv: string;
  delete: string;
  previewDialog: AttachmentPreviewDialogData['translations'];
  executableWarningDialog: {
    title: string;
    message: string;
    agreeLabel: string;
    cancelLabel: string;
  };
}

@Component({
  selector: 'rt-attachment-item',
  templateUrl: './attachment-item.component.html',
  styleUrls: ['./attachment-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.attachment-list]': 'type === "list-item"',
  },
})
export class AttachmentItemComponent {
  public readonly CV_COMPATIBLE_EXTENSIONS: string[] = [
    'doc',
    'docx',
    'pdf',
    'odt',
    'rtf',
    'txt',
    'png',
    'jpg',
    'html',
    'htm',
  ];

  @Input() public type: AttachmentType = 'card';
  @Input() public attachment: Attachment;
  @Input() public attachmentAssignment: MailboxMessageAttachmentAssignment;
  @Input() public hasSetAsCvAction: boolean = false;
  @Input() public hasDeleteAction: boolean = false;
  @Input() public hasDownloadAction: boolean = true;
  @Input() public highlightedFilename: string | null = null;

  @Input() public moreTemplate: TemplateRef<any> | null;
  @Input() public footerIcon?: IconName;
  @Input() public translations: AttachmentItemTranslations = {
    setAsCv: 'rtu.attachment.set_as_cv',
    delete: 'rtu.common.delete',
    previewDialog: {
      closeDialogTooltip: 'rtu.dialog.close_tooltip',
      downloadFile: 'rtu.attachment.download_file',
    },
    executableWarningDialog: {
      title: 'rtu.attachment.executable_warning_dialog.title',
      message: 'rtu.attachment.executable_warning_dialog.message',
      agreeLabel: 'rtu.attachment.executable_warning_dialog.agree_label',
      cancelLabel: 'rtu.attachment.executable_warning_dialog.cancel_label',
    },
  };

  @Output() public setAsCv: EventEmitter<Attachment> = new EventEmitter();
  @Output() public deleteClick: EventEmitter<Attachment> = new EventEmitter();

  public additionalOptsDropdownOpen: boolean = false;
  public actions: AttachmentAction[] = [];
  public href: SafeUrl;
  public thumbnailUrl: string | null;
  public hasThumbnail: boolean;
  public isDocumentOrImage: boolean;
  public hasUploader: boolean;
  public uploaderPhotoUrl: string | undefined;
  public uploaderName: string | undefined;
  public isPrivate: boolean;
  public isExecutable: boolean;

  private thumbnailBroken: boolean = false;

  constructor(
    public sanitizer: DomSanitizer,
    private dialogService: DialogService,
    private viewContainerRef: ViewContainerRef,
    private windowRef: WindowRef,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    const actions: AttachmentAction[] = [];

    if (changes.attachment) {
      this.onAttachmentChange(this.attachment);
    }

    if (
      this.hasSetAsCvAction &&
      this.CV_COMPATIBLE_EXTENSIONS.includes(this.attachment.extension)
    ) {
      actions.push({
        label: this.i18n.translate(this.translations.setAsCv),
        icon: 'file-account',
        variant: 'primary-on-hover',
        handler: () => {
          this.setAsCv.emit(this.attachment);
          this.toggleAdditionalOptsDropdown();
        },
      });
    }

    if (this.hasDeleteAction) {
      actions.push({
        label: this.i18n.translate(this.translations.delete),
        icon: 'delete',
        handler: () => this.delete(),
        variant: 'danger-on-hover',
      });
    }

    this.actions = actions;
  }

  public onAnchorClick(event: MouseEvent) {
    const download = () => this.windowRef.nativeWindow.location.assign(this.attachment.fileUrl);

    if (this.isExecutable) {
      event.preventDefault();
      this.confirmExecutableDownload().subscribe(() => download());
    } else {
      download();
    }
  }

  public thumbnailLoadError(): void {
    this.thumbnailBroken = true;
    this.cdRef.detectChanges();
  }

  public trackByLabel(index: number, action: AttachmentAction): string {
    return action.label;
  }

  public toggleAdditionalOptsDropdown(
    value: boolean = !this.additionalOptsDropdownOpen,
    evt?: MouseEvent,
  ): void {
    if (evt) evt.preventDefault();

    this.additionalOptsDropdownOpen = value;
    this.cdRef.detectChanges();
  }

  public open(): void {
    if (this.isDocumentOrImage) {
      this.dialogService.custom(
        AttachmentPreviewDialogComponent,
        {
          width: DialogWidth.Large,
          closeTooltip: this.translations.previewDialog.closeDialogTooltip,
        },
        <AttachmentPreviewDialogData>{
          attachment: this.attachment,
          translations: this.translations.previewDialog,
        },
        this.viewContainerRef,
      );
    } else if (this.isExecutable) {
      this.confirmExecutableDownload().subscribe(() =>
        this.windowRef.nativeWindow.open(this.attachment.fileUrl),
      );
    } else {
      this.windowRef.nativeWindow.open(this.attachment.fileUrl);
    }
  }

  public delete(): void {
    this.deleteClick.emit(this.attachment);
  }

  private onAttachmentChange(attachment: Attachment) {
    this.thumbnailUrl = this.getThumbnailUrl(attachment);
    this.hasThumbnail = this.thumbnailUrl !== null && !this.thumbnailBroken;
    this.isDocumentOrImage = !!attachment?.pdfUrl || !!attachment?.imageUrl;
    this.hasUploader = attachment.admin || attachment.candidate || attachment.guest;
    this.uploaderPhotoUrl = this.getUploaderPhotoUrl(attachment);
    this.uploaderName = this.getUploaderName(attachment);
    this.isPrivate = this.getIsPrivate(attachment);
    this.isExecutable = isExecutableFile(attachment.filename);
    this.setHref(attachment);
  }

  private setHref(attachment: Attachment): void {
    // eslint-disable-next-line ban/ban
    this.href = this.sanitizer.bypassSecurityTrustUrl(attachment.fileUrl);
  }

  private getThumbnailUrl(attachment: Attachment): string | null {
    return attachment.imageThumbnailUrl || attachment.pdfThumbnailUrl || null;
  }

  private getUploaderPhotoUrl(attachment: Attachment): string | undefined {
    if (attachment.uploader === 'Admin' && attachment.admin) {
      return attachment.admin.photoThumbUrl;
    }
    if (attachment.uploader === 'Candidate' && attachment.candidate) {
      return attachment.candidate.photoThumbUrl;
    }
    if (attachment.uploader === 'Guest' && attachment.guest) {
      return attachment.guest.photoThumbUrl;
    }
    return;
  }

  private getUploaderName(attachment: Attachment): string | undefined {
    if (attachment.uploader === 'Admin' && attachment.admin) {
      return attachment.admin.name || `${attachment.admin.firstName} ${attachment.admin.lastName}`;
    }
    if (attachment.uploader === 'Candidate' && attachment.candidate) {
      return attachment.candidate.name;
    }
    if (attachment.uploader === 'Guest' && attachment.guest) {
      return attachment.guest.name;
    }
    return;
  }

  private getIsPrivate(attachment: Attachment): boolean {
    if (!attachment || !attachment.visibility) {
      return false;
    }

    return attachment.visibility.level !== VisibilityLevel.Public;
  }

  private confirmExecutableDownload() {
    const { title, message, agreeLabel, cancelLabel } = this.translations.executableWarningDialog;
    return this.dialogService
      .warning({
        width: DialogWidth.SmallXX,
        title: this.i18n.translate(title),
        message: this.i18n.translate(message),
        agreeLabel: this.i18n.translate(agreeLabel),
        cancelLabel: this.i18n.translate(cancelLabel),
      })
      .pipe(take(1), filterBoolean);
  }
}
